import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EMPTY_CUSTOM_PRICES } from 'hooks/useConfigPersistence';
import { useToast } from 'hooks/useToast';
import { usePermissions } from './PermissionsProvider';
import {
  useConfigurationLogic, useVendor, usePriceEditions, shouldPromptForNewEdition,
  useExecPricing, useSavedPricePin, priceBookFromEdition, createPriceResolver, useExecMetrics, useDisplayMetrics,
} from './legacy';
import {
  VendorCtx, ConfigInputsCtx, ConfigActionsCtx, MetricsCtx, PricingCtx, BomCtx, CalcStatusCtx, ExecCtx, PriceEditionCtx, ToastCtx,
} from 'shared/state/configContexts';
import type { CustomPrices } from 'shared/state/types';
import type { ToastApi } from 'shared/state/configContexts';

/**
 * The ONE call to useConfigurationLogic in the V2 tree (guidelines §5). Code
 * moved from StorageConfiguratorApp with its comments; nothing here is new
 * logic except grouping the outputs into narrow contexts.
 */
export function ConfigProvider({ children, initialVendorId }: { children: React.ReactNode; initialVendorId?: string }) {
  const { userGroups, isCheckingAccess } = usePermissions();
  // useToast is untyped legacy JS; its `badge` param is optional at runtime
  // (destructured with no default, so an omitted key is simply undefined)
  // even though TS infers it as required from the destructuring alone.
  const toast = useToast() as ToastApi;
  const { showToast } = toast;

  // initialVendor: the first accessible vendor, from ConfiguratorGate. The
  // useConfigurationLogic call below captures its initial config on this first
  // render, so starting on AIC and snapping later would leave config on AIC
  // values (jbodSize 78) that rule R9 cannot index for Dell or Supermicro.
  const vendorApi = useVendor({ userGroups, isCheckingAccess, initialVendor: initialVendorId });
  const { vendor, productDb, metricsDb, switchVendor, canSwitchTo, getVendorConfigOverrides } = vendorApi;

  // CONF-106 project C: prices come from the published edition, not productDb.
  // priceBookFromEdition(null, productDb) returns productDb unchanged.
  const priceEditions = usePriceEditions(vendor.id);
  const priceBook = useMemo(() => priceBookFromEdition(priceEditions.edition, productDb), [priceEditions.edition, productDb]);

  // CONF-106 pt2: exec Custom HW Pricing overrides, mirrored into plain state so
  // they can feed useConfigurationLogic's `customPrices` option.
  // Hook-order cycle & how it's broken: useConfigurationLogic needs customPrices
  // as an input, but useExecPricing (which produces them) needs config.quarter,
  // which only exists AFTER useConfigurationLogic runs. Pattern: useState mirror
  // (one-render lag). useConfigurationLogic gets the committed state; then
  // useExecPricing gets the fresh quarter; useSavedPricePin copies
  // execPricing.customPrices into the state when the memoized reference changes.
  const [customPrices, setCustomPrices] = useState<CustomPrices>(EMPTY_CUSTOM_PRICES as CustomPrices);

  const logic = useConfigurationLogic(priceBook, metricsDb, vendor, { customPrices });
  const { config, system, pricing, viewState, metadata, metrics, bom, pricingData, baselinePricing, isCalculating } = logic;
  const { updateConfig: updateConfigRaw, batchUpdateConfig: batchUpdateConfigRaw, loadConfig: loadConfigRaw } = logic;

  // CONF-212 (App.js parity): exec metric overrides sit AFTER pricing/BOM.
  // `metrics` stays the real figure for every pricing consumer; MetricsCtx
  // publishes `displayMetrics` as `metrics` and the real one as `baselineMetrics`.
  // The hook runs for every user so a loaded quote's overrides display for all.
  const execMetricsRaw = useExecMetrics({
    enabledSets: { afa: !!config.afaEnabled, hybrid: !!config.hybridEnabled },
    throughputBasis: metrics?.throughputBasis,
    onLegacyConverted: (message: string) => showToast({ badge: 'CUSTOM METRICS', title: 'Custom metrics updated', message }),
  });
  // useExecMetrics returns a fresh object each render; its callbacks are stable
  // and changeCount derives from overrides, so overrides is the only real input.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const execMetrics = useMemo(() => execMetricsRaw, [execMetricsRaw.overrides]);
  const { displayMetrics, affectedKeys, staleKeys } = useDisplayMetrics(
    metrics, execMetrics.overrides, { isPBUnit: Boolean(config.isPBUnit) });

  // ONE resolver for every pricing consumer. priceBook, never raw productDb.
  const prices = useMemo(() => createPriceResolver(priceBook, config.quarter, customPrices), [priceBook, config.quarter, customPrices]);

  // "Work in progress" latch for edition revalidation. MUST flip on user-originated
  // changes only — initial computation and vendor switches are not work in progress.
  const configTouched = useRef(false);
  // Last edition number shown (adopted silently or banner-acknowledged).
  const shownEditionRef = useRef<number | null>(null);

  const updateConfig = useCallback((...args: Parameters<typeof updateConfigRaw>) => { configTouched.current = true; return updateConfigRaw(...args); }, [updateConfigRaw]);
  const batchUpdateConfig = useCallback((...args: Parameters<typeof batchUpdateConfigRaw>) => { configTouched.current = true; return batchUpdateConfigRaw(...args); }, [batchUpdateConfigRaw]);
  const loadConfig = useCallback((...args: Parameters<typeof loadConfigRaw>) => { configTouched.current = true; return loadConfigRaw(...args); }, [loadConfigRaw]);

  // Vendor-scope shownEditionRef: each vendor has its own edition sequence.
  // configTouched is deliberately NOT reset here (session-wide latch).
  // Declared BEFORE the adopt/record effect below so effect ordering (React
  // runs a component's effects in declaration order) makes mount resolve as
  // reset (no-op, editionNumber is still null) -> adopt, and a real vendor
  // switch resolve as reset -> adopt the new vendor's number, never the
  // reverse.
  useEffect(() => { shownEditionRef.current = null; }, [vendor.id]);
  // Adopt a newly-fetched edition silently while pristine; otherwise record the
  // first edition seen so shouldPromptForNewEdition has a `previous`.
  useEffect(() => {
    if (priceEditions.editionNumber === null) return;
    if (!configTouched.current) shownEditionRef.current = priceEditions.editionNumber;
    else if (shownEditionRef.current === null) shownEditionRef.current = priceEditions.editionNumber;
  }, [priceEditions.editionNumber]);

  // Exec pricing session (kept alive once the disclosure has ever been opened).
  const execPricingTouched = useRef(false);
  const loadedQuoteIdRef = useRef<string | null>(null);
  const [execPanelOpen, setExecPanelOpen] = useState(false);
  const execPricing = useExecPricing({
    enabled: execPanelOpen || execPricingTouched.current,
    vendor: vendor.id,
    quarter: config?.quarter,
    asOf: priceEditions.edition?.asOf,
    onOverridesReset: (reason) => showToast({ badge: 'EXEC PRICING', title: 'Custom pricing reset', message: reason }),
  });
  const { pinSavedPrices, releasePin } = useSavedPricePin(execPricing, setCustomPrices);

  // handleVendorChange (App.js) minus the consultation reset, which TierProvider derives.
  const changeVendor = useCallback((newVendorId: string) => {
    if (!canSwitchTo(newVendorId)) return;
    if (!switchVendor(newVendorId)) return;
    // CONF-212/CONF-258 (App.js handleVendorChange): overrides are per-node
    // throughput ratings, and a different vendor's nodes rate differently, so
    // a USER vendor switch drops them. Deliberately not an effect on
    // vendor.id — loadSavedConfig switches vendor and then hydrates in the
    // same click, and an effect would wipe what hydrate just set.
    execMetrics.resetAll();
    const overrides = getVendorConfigOverrides(newVendorId);
    // RAW setter: applying vendor defaults is not user work in progress.
    batchUpdateConfigRaw(overrides);
  }, [canSwitchTo, switchVendor, getVendorConfigOverrides, batchUpdateConfigRaw, execMetrics]);

  const vendorValue = useMemo(() => ({ ...vendorApi, changeVendor }), [vendorApi, changeVendor]);
  const inputs = useMemo(() => ({ config, system, pricing, viewState, metadata }), [config, system, pricing, viewState, metadata]);
  const actions = useMemo(() => ({ updateConfig, batchUpdateConfig, loadConfig, updateConfigRaw, batchUpdateConfigRaw }),
    [updateConfig, batchUpdateConfig, loadConfig, updateConfigRaw, batchUpdateConfigRaw]);
  const metricsValue = useMemo(() => ({
    metrics: displayMetrics, baselineMetrics: metrics, affectedKeys, staleKeys,
  }), [displayMetrics, metrics, affectedKeys, staleKeys]);
  const pricingValue = useMemo(() => ({ pricingData, baselinePricing, priceBook, prices, priceEditions }), [pricingData, baselinePricing, priceBook, prices, priceEditions]);
  const bomValue = useMemo(() => ({ bom }), [bom]);
  const calcValue = useMemo(() => ({ isCalculating }), [isCalculating]);
  const execValue = useMemo(() => ({
    execPricing, customPrices, setCustomPrices, pinSavedPrices, releasePin, loadedQuoteIdRef, execPricingTouched, execPanelOpen, setExecPanelOpen,
    execMetrics,
  }), [execPricing, customPrices, pinSavedPrices, releasePin, execPanelOpen, execMetrics]);
  const editionValue = useMemo(() => ({
    promptNewEdition: shouldPromptForNewEdition({ touched: configTouched.current, previous: shownEditionRef.current, next: priceEditions.editionNumber }),
    staleWarning: priceEditions.staleWarning, status: priceEditions.status, refresh: priceEditions.refresh, editionNumber: priceEditions.editionNumber,
    // `config` isn't read above, but its reference change is exactly what
    // makes this memo re-run right after a ref-only mutation
    // (configTouched.current) flips; without it the prompt would lag a
    // render behind the touch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [priceEditions, config]);

  return (
    <ToastCtx.Provider value={toast}>
      <VendorCtx.Provider value={vendorValue}>
        <ConfigInputsCtx.Provider value={inputs}>
          <ConfigActionsCtx.Provider value={actions}>
            <MetricsCtx.Provider value={metricsValue}>
              <PricingCtx.Provider value={pricingValue}>
                <BomCtx.Provider value={bomValue}>
                  <CalcStatusCtx.Provider value={calcValue}>
                    <ExecCtx.Provider value={execValue}>
                      <PriceEditionCtx.Provider value={editionValue}>{children}</PriceEditionCtx.Provider>
                    </ExecCtx.Provider>
                  </CalcStatusCtx.Provider>
                </BomCtx.Provider>
              </PricingCtx.Provider>
            </MetricsCtx.Provider>
          </ConfigActionsCtx.Provider>
        </ConfigInputsCtx.Provider>
      </VendorCtx.Provider>
    </ToastCtx.Provider>
  );
}
